import type { PipelineStageState, ScoringRunStatus } from '../types';

export type StatusTone = 'high' | 'medium' | 'low' | 'neutral';

export function runStatusLabel(status: ScoringRunStatus | null | undefined): string {
  switch (status) {
    case 'failed':
      return 'Failed';
    case 'completed':
      return 'Completed';
    case 'running':
      return 'In progress';
    case 'queued':
      return 'Queued';
    case 'skipped':
      return 'Skipped';
    default:
      return status ? status.replace(/_/g, ' ') : 'No runs yet';
  }
}

export function runStatusTone(status: ScoringRunStatus | null | undefined): StatusTone {
  switch (status) {
    case 'failed':
      return 'low';
    case 'completed':
      return 'high';
    case 'running':
    case 'queued':
      return 'medium';
    default:
      return 'neutral';
  }
}

export function hubSyncStatus(row: {
  success: boolean;
  skipped: boolean;
}): { label: string; tone: StatusTone } {
  if (row.skipped) return { label: 'Skipped', tone: 'neutral' };
  if (row.success) return { label: 'Completed', tone: 'high' };
  return { label: 'Failed', tone: 'low' };
}

export function hubSyncSummary(hub: {
  succeeded: boolean | null;
  skipped?: boolean;
} | null | undefined): { label: string; tone: StatusTone } {
  if (!hub || hub.succeeded == null) return { label: 'No runs yet', tone: 'neutral' };
  if (hub.skipped) return { label: 'Skipped', tone: 'neutral' };
  if (hub.succeeded) return { label: 'Completed', tone: 'high' };
  return { label: 'Failed', tone: 'low' };
}

export function stageLabel(stage: string | null | undefined): string {
  switch (stage) {
    case 'summarizing':
      return 'Summarizing';
    case 'classifying':
      return 'Classifying';
    case 'scoring':
      return 'Scoring';
    case 'finalizing':
      return 'Finalizing';
    default:
      return stage ? stage.replace(/_/g, ' ') : '—';
  }
}

export function stageRowStateLabel(state: PipelineStageState): string {
  switch (state) {
    case 'done':
      return 'Done';
    case 'failed':
      return 'Failed';
    case 'running':
      return 'In progress';
    default:
      return 'Not started';
  }
}

export function stageRowTone(state: PipelineStageState): StatusTone {
  switch (state) {
    case 'done':
      return 'high';
    case 'failed':
      return 'low';
    case 'running':
      return 'medium';
    default:
      return 'neutral';
  }
}

export function failureCountTone(
  failures: number,
  status: { success: boolean; skipped: boolean }
): 'low' | 'medium' | 'none' {
  if (failures <= 0) return 'none';
  if (!status.success && !status.skipped) return 'low';
  return 'medium';
}

export function waitingAccent(count: number): 'warning' | 'success' {
  return count > 0 ? 'warning' : 'success';
}
